import {
  compareSortValues,
  toggleSort,
  type SortState,
} from "@/components/dataTable/tableSort";
import type { TimesheetRowRecord } from "@/features/timesheetFlow/timesheetFlowTypes";

export type TimesheetTimeSortKey =
  | "work_date"
  | "person_name"
  | "hours_decimal"
  | "time_from"
  | "time_to"
  | "comment";

export const DEFAULT_TIMESHEET_TIME_SORT: SortState = {
  key: "work_date",
  direction: "asc",
};

const SORT_KEYS = new Set<string>([
  "work_date",
  "person_name",
  "hours_decimal",
  "time_from",
  "time_to",
  "comment",
]);

export function normalizeTimesheetTimeSort(key: unknown, direction: unknown): SortState {
  const safeKey = typeof key === "string" && SORT_KEYS.has(key) ? key : DEFAULT_TIMESHEET_TIME_SORT.key;
  const safeDirection = direction === "desc" ? "desc" : "asc";
  return { key: safeKey, direction: safeDirection };
}

export function timesheetHoursNumber(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const numeric = Number(String(value ?? "").trim().replace(",", "."));
  return Number.isFinite(numeric) ? numeric : 0;
}

export function formatTimesheetHours(value: unknown): string {
  const hours = timesheetHoursNumber(value);
  const rounded = Math.round((hours + Number.EPSILON) * 100) / 100;
  return String(rounded).replace(".", ",");
}

export function sumTimesheetHours(rows: ReadonlyArray<{ hours_decimal: unknown }>): number {
  const cents = rows.reduce(
    (sum, row) => sum + Math.round(timesheetHoursNumber(row.hours_decimal) * 100),
    0,
  );
  return cents / 100;
}

function sortValue(row: TimesheetRowRecord, key: string): string | number {
  switch (key) {
    case "hours_decimal":
      return timesheetHoursNumber(row.hours_decimal);
    case "person_name":
      return row.person_name || "";
    case "work_date":
      return row.work_date || "";
    case "time_from":
      return row.time_from || "";
    case "time_to":
      return row.time_to || "";
    case "comment":
      return row.comment || "";
    default:
      return "";
  }
}

export type IndexedTimesheetRow<T extends TimesheetRowRecord = TimesheetRowRecord> = {
  row: T;
  index: number;
};

/** Sortiert Zeiten für die Tabelle und für die PDF (gleiche Reihenfolge). */
export function sortTimesheetTimeEntries<T extends IndexedTimesheetRow>(
  entries: readonly T[],
  sort: SortState,
): T[] {
  const active = normalizeTimesheetTimeSort(sort.key, sort.direction);
  const factor = active.direction === "asc" ? 1 : -1;
  return [...entries].sort((left, right) => {
    const primary = compareSortValues(sortValue(left.row, active.key), sortValue(right.row, active.key));
    if (primary !== 0) return factor * primary;
    if (active.key !== "work_date") {
      const byDate = compareSortValues(left.row.work_date || "", right.row.work_date || "");
      if (byDate !== 0) return byDate;
    }
    if (active.key !== "person_name") {
      const byName = compareSortValues(left.row.person_name || "", right.row.person_name || "");
      if (byName !== 0) return byName;
    }
    if (active.key !== "time_from") {
      const byTime = compareSortValues(left.row.time_from || "", right.row.time_from || "");
      if (byTime !== 0) return byTime;
    }
    return left.index - right.index;
  });
}

export function toggleTimesheetTimeSort(current: SortState, key: string): SortState {
  const initialDirection = key === "hours_decimal" ? "desc" : "asc";
  return toggleSort(current, key, initialDirection);
}

export type MergedTimesheetTimeEntry<T extends TimesheetRowRecord = TimesheetRowRecord> =
  IndexedTimesheetRow<T> & { sourceIndices: number[] };

function timesheetDayPersonKey(row: TimesheetRowRecord): string {
  const date = (row.work_date || "").slice(0, 10);
  const person = (row.person_name || "").trim().toLocaleLowerCase("de-DE");
  return `${date}::${person}`;
}

/**
 * Ohne angezeigte Uhrzeiten wirken zwei Einsätze derselben Person am selben Tag
 * wie doppelte Zeilen. Die Stunden werden addiert, die Herkunftszeilen bleiben erhalten.
 */
export function mergeTimesheetDayEntries<T extends TimesheetRowRecord>(
  entries: readonly IndexedTimesheetRow<T>[],
): MergedTimesheetTimeEntry<T>[] {
  const groups = new Map<string, {
    row: T;
    index: number;
    sourceIndices: number[];
    comments: string[];
  }>();
  const order: string[] = [];

  for (const entry of entries) {
    const key = timesheetDayPersonKey(entry.row);
    const comment = (entry.row.comment || "").trim();
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        row: {
          ...entry.row,
          time_from: null,
          time_to: null,
          hours_decimal: timesheetHoursNumber(entry.row.hours_decimal),
        },
        index: entry.index,
        sourceIndices: [entry.index],
        comments: comment ? [comment] : [],
      });
      order.push(key);
      continue;
    }
    existing.sourceIndices.push(entry.index);
    existing.row = {
      ...existing.row,
      hours_decimal: sumTimesheetHours([
        { hours_decimal: existing.row.hours_decimal },
        { hours_decimal: entry.row.hours_decimal },
      ]),
    };
    if (comment && !existing.comments.includes(comment)) existing.comments.push(comment);
  }

  return order.map((key) => {
    const group = groups.get(key)!;
    return {
      row: { ...group.row, comment: group.comments.join(" · ") },
      index: group.index,
      sourceIndices: group.sourceIndices,
    };
  });
}
